import React, {
  useReducer, useEffect, useState, useContext, useMemo,
} from 'react';
import { useQuery } from '@apollo/client';
import { ERROR_MESSAGE } from '../../../components/Messages/Messages';
import GET_COLLECTION_DATA_QUERY from '../gql/collection.gql';
import QuickviewLoading from '../QuickviewLoading/QuickviewLoading';
import ProductPageContext from '../../../contexts/ProductPageContext';
import ProductNameAndDescription from '../../../components/ProductNameAndDescription/ProductNameAndDescription';
import QuickviewImages from '../QuickviewImages/QuickviewImages';
import QuickviewAddToBagForm from '../QuickviewAddToBagForm/QuickviewAddToBagForm';
import SizeGuideModal from '../../../components/SizeGuideModal/SizeGuideModal';
import ProductPageReducer from '../../ProductPage/ProductPageReducer';
import BreakpointContext from '../../../contexts/BreakpointContext';
import SoldOutPage from '../../SoldOutPage/SoldOutPage';
import ExchangesQuickviewAddToBagForm from '../ExchangesQuickviewAddToBagForm/ExchangesQuickviewAddToBagForm';
import ExchangesQuickviewNameAndDescription from '../../../components/ExchangesQuickviewNameAndDescription/ExchangesQuickviewNameAndDescription';
import ReviewScript from '../../../features/ProductReviews/ReviewScript';

export default function QuickviewContent({
  collectionId,
  productId,
  sku,
  faceout,
  isStylitics,
  type,
  orderItemId,
  orderLineKeyId,
  store,
  storeId,
  handleCloseModal,
  onModalOpenCallback,
  onModalPrimaryActionSuccessCallback,
  pageViewMethod,
  productViewMethod,
  dataText,
  dataAction,
}) {
  const [openSizeGuide, setSizeGuideOpen] = useState(false);
  const [onModalOpenCallbackHasRun, setOnModalOpenCallbackHasRun] = useState(false);
  const swatchChanged = ({ target: { value } }) => setActiveProdId(value);
  const breakpoints = useContext(BreakpointContext);

  const setActiveProductHasSocialProofing = (hasSocialProofing = false) => {
    dispatch({
      type: 'UPDATE_ACTIVE_PRODUCT_SOCIAL_PROOFING',
      payload: {
        hasSocialProofing,
      },
    });
  };

  const [ProductPageState, dispatch] = useReducer(ProductPageReducer, {
    collectionId,
    productId,
    faceout,
    sku,
    type,
    isStylitics,
    pageViewMethod,
    productViewMethod,
    dataText,
    dataAction,
    actions: {
      setActiveProductHasSocialProofing,
    },
  });

  const setActiveProdId = (newProductId) => {
    dispatch({
      type: 'UPDATE_ACTIVE_PRODUCT',
      payload: newProductId,
    });
  };

  const setSelectedPrimarySize = (selectedSize) => {
    dispatch({
      type: 'UPDATE_SELECTED_SIZE',
      payload: {
        sizeType: 'primary',
        value: selectedSize,
        isSticky: false,
      },
    });
  };

  const setSelectedSecondarySize = (selectedSize) => {
    dispatch({
      type: 'UPDATE_SELECTED_SIZE',
      payload: {
        sizeType: 'secondary',
        value: selectedSize,
        isSticky: false,
      },
    });
  };

  const setStickySelectedPrimarySize = (selectedSize) => {
    dispatch({
      type: 'UPDATE_SELECTED_SIZE',
      payload: {
        sizeType: 'primary',
        value: selectedSize,
        isSticky: true,
      },
    });
  };

  const setStickySelectedSecondarySize = (selectedSize) => {
    dispatch({
      type: 'UPDATE_SELECTED_SIZE',
      payload: {
        sizeType: 'secondary',
        value: selectedSize,
        isSticky: true,
      },
    });
  };

  const {
    error, loading, data, previousData,
  } = useQuery(GET_COLLECTION_DATA_QUERY, {
    variables: {
      collectionId: `${collectionId}`,
      productId: `${productId}`,
      faceout: `${faceout}`,
      categoryId: `${ProductPageState.categoryId}`,
    },
    context: { batch: true },
    skip: !collectionId,
    keyArgs: ['collectionId', 'productId'],
  });

  useEffect(() => {
    if (!ProductPageState.collectionData || onModalOpenCallbackHasRun) return;
    setOnModalOpenCallbackHasRun(true);
    if (typeof onModalOpenCallback === 'function') {
      onModalOpenCallback();
    }
    document.dispatchEvent(new CustomEvent('quickview:recs:openModal'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ProductPageState.collectionData]);

  const queryData = data || previousData;

  const contextValue = useMemo(() => ({
    ...ProductPageState,
    quickview: true,
    quickviewType: type,
  }), [ProductPageState, type]);

  if (queryData && queryData.collection.collection && queryData.collection.collection.isSoldOut) {
    return <SoldOutPage queryData={queryData} collectionId={collectionId} />;
  }

  if (queryData && queryData.collection && !ProductPageState.collectionData) {
    dispatch({
      type: 'INITIAL_QUERY_COMPLETE',
      payload: queryData,
    });
  }

  const isMobile = breakpoints.xxsmall && !breakpoints.medium;
  const hasBazaarVoiceEnable = ProductPageState?.reviewData?.hasBazaarVoiceEnable ?? false;

  const renderProductNameAndDescription = () => {
    if (!isMobile) return null;

    if (type === 'exchange') {
      const { activeProduct } = ProductPageState.collectionData || {};
      return (
        <ExchangesQuickviewNameAndDescription
          activeProduct={activeProduct}
          exchangeColorAndSize={ProductPageState?.tmntData?.exchangeColorAndSize?.value}
        />
      );
    }

    return (
      <ProductNameAndDescription isQuickView />
    );
  };

  const AddToBagForm = type === 'exchange'
    ? ExchangesQuickviewAddToBagForm
    : QuickviewAddToBagForm;

  function renderQuickviewContent() {
    if (error) {
      return <div data-testid="error-message">{ERROR_MESSAGE}</div>;
    }

    if (loading && !queryData) {
      return <QuickviewLoading isMobile={isMobile} />;
    }

    return (
      <div className="quickview-modal-wrapper">
        {hasBazaarVoiceEnable && (<ReviewScript />)}
        {renderProductNameAndDescription()}
        <div className="quickview-image-container">
          <QuickviewImages isMobile={isMobile} />
        </div>
        <section className="quickview-add-to-bag-container quickview-content-scroll">
          <AddToBagForm
            swatchChanged={swatchChanged}
            categoryId={ProductPageState.categoryId}
            handlePrimarySizeUpdate={setSelectedPrimarySize}
            handleSecondarySizeUpdate={setSelectedSecondarySize}
            handleStickyPrimarySizeUpdate={setStickySelectedPrimarySize}
            handleStickySecondarySizeUpdate={setStickySelectedSecondarySize}
            reviewData={ProductPageState.reviewData}
            shippingContent={ProductPageState.shippingContent}
            siteRegion="US"
            eSpotContent={ProductPageState.eSpotContent}
            setSizeGuideOpen={setSizeGuideOpen}
            orderItemId={orderItemId}
            orderLineKeyId={orderLineKeyId}
            store={store}
            storeId={storeId}
            handleCloseModal={handleCloseModal}
            onModalPrimaryActionSuccessCallback={onModalPrimaryActionSuccessCallback}
            preselectExchangeProductId={productId}
          />

          {ProductPageState.collectionData?.activeProduct && (
            <SizeGuideModal
              openSizeGuide={openSizeGuide}
              eSpotContent={ProductPageState.eSpotContent}
              setSizeGuideOpen={setSizeGuideOpen}
            />
          )}
        </section>
      </div>
    );
  }

  return (
    <ProductPageContext.Provider value={contextValue}>
      {renderQuickviewContent()}
    </ProductPageContext.Provider>
  );
}
